import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import NodeForm from "./component-form/node-form";
import ExceptionsSidebarWrapper from "./exceptions/exceptions-sidebar-wrapper";
import MetadataBrowserWrapper from "./metadata-browser/metadata-browser-wrapper";
import AppErrorsSidebar from "./app-errors/app-errors-sidebar";
import AppSettingsSidebar from "./app-settings/app-settings-sidebar";
import ValidationErrorsSidebar from "./validation-errors/validation-errors-sidebar";
import sxClasses from "../flowdesigner.styles";
import { useAppSelector } from "src/stores/redux/hooks";
import {
    getAppModel,
    getAppStatus,
    getIsSidebarFullWidth,
    getIsRightSidebarOpen,
    getIsExceptionsSidebarOpen,
    getIsMetadataBrowserOpen,
    getIsAppErrorsSidebarOpen,
    getIsValidationErrorsSidebarOpen,
    getIsAppSettingsSidebarOpen
} from "../helpers/selectors";
import { SidebarWrapperRef } from "./component-form/sidebar-wrapper";

interface ResizableSidebarProps {
    promptIfUnsavedChanged: () => Promise<boolean>;
    sidebarRef: React.RefObject<SidebarWrapperRef>;
    appSettingsRef: React.RefObject<any>;
}

interface ActiveSidebarProps extends ResizableSidebarProps {
    isRightSidebarOpen: boolean;
    isExceptionsSidebarOpen: boolean;
    isMetadataBrowserOpen: boolean;
    isAppErrorsSidebarOpen: boolean;
    isValidationErrorsSidebarOpen: boolean;
    isAppSettingsSidebarOpen: boolean;
}

const ActiveSidebar: React.FC<ActiveSidebarProps> = ({
    sidebarRef,
    promptIfUnsavedChanged,
    appSettingsRef,
    isRightSidebarOpen,
    isExceptionsSidebarOpen,
    isMetadataBrowserOpen,
    isAppErrorsSidebarOpen,
    isValidationErrorsSidebarOpen,
    isAppSettingsSidebarOpen
}) => {
    const appModel = useAppSelector(getAppModel);
    const appStatus = useAppSelector(getAppStatus);

    if (isRightSidebarOpen)
        return (
            <NodeForm
                ref={sidebarRef}
                promptIfUnsavedChanged={promptIfUnsavedChanged}
                appModel={appModel}
                appStatus={appStatus}
            />
        );
    if (isExceptionsSidebarOpen) return <ExceptionsSidebarWrapper appModel={appModel} />;
    if (isMetadataBrowserOpen) return <MetadataBrowserWrapper app={appModel} />;
    if (isAppErrorsSidebarOpen) return <AppErrorsSidebar app={appModel} appStatus={appStatus ?? undefined} />;
    if (isValidationErrorsSidebarOpen) return <ValidationErrorsSidebar app={appModel} />;
    if (isAppSettingsSidebarOpen)
        return <AppSettingsSidebar ref={appSettingsRef} app={appModel} appStatus={appStatus ?? undefined} />;
    return null;
};

const ResizableSidebar = ({ promptIfUnsavedChanged, sidebarRef, appSettingsRef }: ResizableSidebarProps) => {
    const rightSidebarWrapperRef = useRef<HTMLDivElement>(null);
    const isResizingRightSidebarRef = useRef(false);
    const rightSidebarStartXRef = useRef(0);
    const rightSidebarStartWidthRef = useRef(0);

    const isSidebarFullWidth = useAppSelector(getIsSidebarFullWidth);
    const isRightSidebarOpen = useAppSelector(getIsRightSidebarOpen);
    const isExceptionsSidebarOpen = useAppSelector(getIsExceptionsSidebarOpen);
    const isMetadataBrowserOpen = useAppSelector(getIsMetadataBrowserOpen);
    const isAppErrorsSidebarOpen = useAppSelector(getIsAppErrorsSidebarOpen);
    const isValidationErrorsSidebarOpen = useAppSelector(getIsValidationErrorsSidebarOpen);
    const isAppSettingsSidebarOpen = useAppSelector(getIsAppSettingsSidebarOpen);

    const showSidebar = useMemo(
        () =>
            isRightSidebarOpen ||
            isExceptionsSidebarOpen ||
            isMetadataBrowserOpen ||
            isAppErrorsSidebarOpen ||
            isValidationErrorsSidebarOpen ||
            isAppSettingsSidebarOpen,
        [
            isRightSidebarOpen,
            isExceptionsSidebarOpen,
            isMetadataBrowserOpen,
            isAppErrorsSidebarOpen,
            isValidationErrorsSidebarOpen,
            isAppSettingsSidebarOpen
        ]
    );

    const [rightSidebarWidth, setRightSidebarWidth] = useState<number>(400);

    // Handle resizing of the right sidebar (component form / settings / errors)
    const handleRightSidebarResizeMouseDown = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (isSidebarFullWidth) return; // Disable drag when full-width
            event.preventDefault();
            isResizingRightSidebarRef.current = true;
            rightSidebarStartXRef.current = event.clientX;
            rightSidebarStartWidthRef.current =
                rightSidebarWrapperRef.current?.getBoundingClientRect().width || rightSidebarWidth;
        },
        [rightSidebarWidth, isSidebarFullWidth]
    );

    // Global mouse listeners for drag-to-resize behavior
    useEffect(() => {
        const handleMouseMove = (event: MouseEvent) => {
            if (!isResizingRightSidebarRef.current) return;
            const deltaX = rightSidebarStartXRef.current - event.clientX;
            let nextWidth = rightSidebarStartWidthRef.current + deltaX;
            const minWidth = 400; // 400px
            if (nextWidth < minWidth) nextWidth = minWidth;
            const maxWidth = window.innerWidth * 0.4; // 40vw
            if (nextWidth > maxWidth) nextWidth = maxWidth;
            setRightSidebarWidth(nextWidth);
        };

        const handleMouseUp = () => {
            if (isResizingRightSidebarRef.current) {
                isResizingRightSidebarRef.current = false;
            }
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, []);

    if (!showSidebar) return null;

    return (
        <>
            {!isSidebarFullWidth && (
                <Box sx={sxClasses.resizableSidebarBorder} onMouseDown={handleRightSidebarResizeMouseDown} />
            )}

            <Box
                ref={rightSidebarWrapperRef}
                sx={{
                    ...sxClasses.rightSidebarWrapper,
                    width: isSidebarFullWidth ? "100%" : rightSidebarWidth,
                    maxWidth: isSidebarFullWidth ? "100%" : sxClasses.rightSidebarWrapper.maxWidth
                }}
                className="right-sidebar-wrapper"
                data-test-id={`${isSidebarFullWidth ? "fullscreen" : "right"}-sidebar-wrapper`}
            >
                <ActiveSidebar
                    sidebarRef={sidebarRef}
                    promptIfUnsavedChanged={promptIfUnsavedChanged}
                    appSettingsRef={appSettingsRef}
                    isRightSidebarOpen={isRightSidebarOpen}
                    isExceptionsSidebarOpen={isExceptionsSidebarOpen}
                    isMetadataBrowserOpen={isMetadataBrowserOpen}
                    isAppErrorsSidebarOpen={isAppErrorsSidebarOpen}
                    isValidationErrorsSidebarOpen={isValidationErrorsSidebarOpen}
                    isAppSettingsSidebarOpen={isAppSettingsSidebarOpen}
                />
            </Box>
        </>
    );
};

export default ResizableSidebar;
